import axios from 'axios';

/**
 * Media URLs are always stored as `/media?key=<raw key>` with `key` as the sole,
 * final query parameter (see `getSignedUploadData` in the API). A raw filename can
 * legitimately contain a literal `&` (e.g. "Testimonial KRISS 1&2.pdf"), so we can't
 * split on `&` to find the end of the key - that would truncate the key at the first
 * literal `&` and produce a signed URL for the wrong S3 object. Instead we treat
 * everything after the first `key=` as the raw key value.
 */
function extractKeyValue(url: string): { prefix: string; keyValue: string } | null {
  const idx = url.indexOf('key=');
  if (idx === -1) return null;
  return { prefix: url.slice(0, idx + 'key='.length), keyValue: url.slice(idx + 'key='.length) };
}

/**
 * Encode the `key` query segment so filenames with `#`, `&`, etc. work with signed URL requests.
 * (Same behavior as previously in UploadActions.)
 */
export function encodeMediaUrl(url: string): string {
  const match = extractKeyValue(url);
  if (!match) return url;

  return `${match.prefix}${encodeURIComponent(match.keyValue)}`;
}

const isLocalAssetPath = (src: string): boolean =>
  src.startsWith('/assets/') || src.startsWith('/static/') || src.startsWith('/_next/');

/**
 * Portal images are stored as `/media?key=<s3-key>`, not as static files.
 * `/assets/...` is a Next public file. Everything else needs a signed GET via the API.
 */
export const resolveSignedImageRequest = (
  src: string
): { kind: 'direct'; src: string } | { kind: 'sign'; requestUrl: string } => {
  if (src.startsWith('http') || isLocalAssetPath(src)) {
    return { kind: 'direct', src };
  }
  if (src.startsWith('/media')) {
    const encoded = encodeMediaUrl(src);
    const requestUrl = encoded.includes('download=') ? encoded : `${encoded}&download=false`;
    return { kind: 'sign', requestUrl };
  }
  return { kind: 'sign', requestUrl: `/media?key=${encodeURIComponent(src)}&download=false` };
};

/**
 * Best-effort display name from a portal media URL (`/media?key=.../filename.pdf`).
 */
export function getFileDisplayNameFromMediaUrl(uploadUrl: string | undefined): string | null {
  if (!uploadUrl?.trim()) return null;
  const match = extractKeyValue(uploadUrl);
  if (!match) return null;
  try {
    // Raw stored keys aren't percent-encoded, but decodeURIComponent is a no-op on plain text.
    const key = decodeURIComponent(match.keyValue);
    const segments = key.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : null;
  } catch {
    return null;
  }
}

export async function openSignedMediaUrl(uploadUrl: string, download = true): Promise<void> {
  if (!uploadUrl?.trim()) return;
  let encodedUrl = encodeMediaUrl(uploadUrl);
  encodedUrl = `${encodedUrl}&download=${download.toString()}`;
  const { data } = await axios.get(encodedUrl);
  if (data.signedURL) window.open(data.signedURL, '_blank');
}
